<template>
  <div
    v-if="isConfigured"
    ref="wrapper"
    class="dn-audio-playlist"
  >
    <p class="dn-audio-playlist__kicker">
      {{ publicConfig.title }}
    </p>
    <h3 class="dn-audio-playlist__title">
      {{ title || t("Listen to today's most read stories") }}
    </h3>
    <div class="dn-audio-playlist__player-wrap">
      <div
        v-if="canListenToAudio"
        ref="playerContainer"
        class="dn-audio-playlist__player"
      />
      <div
        v-else
        class="dn-audio-playlist__placeholder"
      >
        <button
          type="button"
          class="dn-audio-playlist__placeholder-button"
          :aria-label="t('Listen')"
          @click="showLoginTooltip"
        >
          <svg
            viewBox="0.6667 0.6667 26.6667 26.6667"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            class="dn-audio-playlist__placeholder-icon"
          >
            <path
              d="M11.3333 18.6667V9.33341C11.3333 8.78675 11.96 8.46675 12.4 8.80008L18.6266 13.4667C18.9866 13.7334 18.9866 14.2667 18.6266 14.5334L12.4 19.2001C11.96 19.5334 11.3333 19.2134 11.3333 18.6667Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <p class="dn-audio-playlist__placeholder-title">
          {{ t('Listen') }}
        </p>
      </div>
      <DnTooltip
        v-if="!canListenToAudio && isLoginTooltipOpen"
        :key="loginTooltipKey"
        ref="loginTooltipRef"
        class="dn-audio-playlist__login-tooltip"
      >
        <template #content>
          <div class="dn-audio-playlist__login-tooltip-card">
            <button
              type="button"
              class="dn-audio-playlist__login-tooltip-close"
              :aria-label="t('Close')"
            >
              ×
            </button>
            <h3 class="dn-audio-playlist__login-tooltip-title">
              {{ t('Listen to the stories') }}
            </h3>
            <p class="dn-audio-playlist__login-tooltip-text">
              {{ loginTooltipText }}
            </p>
            <a
              v-track-event="loginTooltipCtaTrackEvent"
              :href="loginTooltipCtaHref"
              class="dn-audio-playlist__login-tooltip-cta"
            >
              {{ loginTooltipCtaText }}
            </a>
          </div>
        </template>
        <span
          class="dn-audio-playlist__tooltip-anchor"
          aria-hidden="true"
        />
      </DnTooltip>
    </div>
    <details>
      <summary class="dn-audio-playlist__disclaimer-button">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          role="img"
          class="dn-audio-playlist__disclaimer-icon"
        >
          <title>{{ t('Disclaimer') }}</title>
          <rect
            width="23"
            height="23"
            x=".5"
            y=".5"
            class="dn-audio-playlist__disclaimer-icon-stroke"
            rx="11.5"
          />
          <path
            class="dn-audio-playlist__disclaimer-icon-fill"
            fill-rule="evenodd"
            d="M12.003 7.883c.387 0 .701-.31.701-.692a.696.696 0 0 0-.7-.691.696.696 0 0 0-.702.691c0 .382.314.692.701.692M10 9.803h2.353a.5.5 0 0 1 .5.5v5.032h2.104v1H9.75v-1h2.103v-4.532H10z"
            clip-rule="evenodd"
          />
        </svg>
      </summary>
      <p class="dn-audio-playlist__disclaimer-text">
        {{ t('This AI-generated voice has not affected journalism. Some words may be pronounced imprecisely.') }}
        <dn-link
          v-if="disclaimerLink"
          :href="disclaimerLink"
          :external="true"
          target="_blank"
          class="dn-audio-playlist__disclaimer-link"
        >
          {{ t('Read more here') }}.
        </dn-link>
      </p>
    </details>
  </div>
</template>

<script setup lang="ts">
import type { PublicationContext } from '~/types';

/*
 * Frontpage "listen to today's most read stories" player. Rendered from a
 * Dr.Edition placeholder (see DnPlaceholder), so CMS fields arrive as props;
 * `title` and `streamId` optionally override the heading and the Kilkaya
 * stream the playlist is built from.
 */
const props = defineProps({
  title: {
    type: String,
    required: false,
    default: ''
  },
  streamId: {
    type: String,
    required: false,
    default: ''
  },
  slots: {
    type: Array,
    required: false,
    default: () => []
  }
});

const { t } = useI18n();
const publicConfig = useRuntimeConfig().public as unknown as PublicationContext;
const { disclaimerLink = '' } = publicConfig.beyondWords || {};
const { genericEvent } = useTrackingEvent();

const audioPlaylistSource = 'audio_playlist_front';
const DESKTOP_WIDGET_WIDTH = '24rem';

const wrapper = ref<HTMLDivElement | null>(null);
const playerContainer = ref<HTMLDivElement | null>(null);

const {
  canListen: canListenToAudio,
  needsSubscription,
  loginTooltipCtaHref,
  ctaTrackEvent: loginTooltipCtaTrackEvent,
  trackingTarget
} = useAudioAccess(audioPlaylistSource);

const {
  tooltipRef: loginTooltipRef,
  isOpen: isLoginTooltipOpen,
  tooltipKey: loginTooltipKey,
  show: showLoginTooltip,
  hide: hideLoginTooltip
} = useAudioLoginTooltip('.dn-audio-playlist__login-tooltip-close');

const { playlistItems, loading: playlistLoading } = useAudioPlaylist({
  streamId: () => props.streamId,
  enabled: canListenToAudio
});

const { isConfigured, create, destroy } = useBeyondWordsPlayer({
  container: playerContainer,
  colorScope: wrapper,
  colorVars: { bg: '--dn-audio-playlist-player-bg', fg: '--dn-audio-playlist-player-fg' },
  desktopWidgetWidth: DESKTOP_WIDGET_WIDTH,
  revealWidgetOnPlay: true,
  onPlay: () => {
    genericEvent({
      eventName: 'Audio',
      component: 'audio-player',
      elementName: 'dn-audio-play-button',
      elementId: 'audio-playlist-play',
      source: audioPlaylistSource,
      target: trackingTarget.value
    });
  },
  onTrackChange: ({ sourceId, auto }) => {
    genericEvent({
      eventName: 'Audio',
      component: 'audio-player',
      elementName: 'dn-audio-track-change',
      elementId: 'audio-playlist-track-change',
      action: auto ? 'auto_advance' : 'skip',
      source: audioPlaylistSource,
      target: sourceId || trackingTarget.value
    });
  }
});

const loginTooltipText = computed(() => {
  if (needsSubscription.value) {
    return t('You need an active subscription to listen to these stories.');
  }

  return t('You must be logged in with an active subscription to listen to these stories.');
});

const loginTooltipCtaText = computed(() => (needsSubscription.value ? t('Subscribe') : t('Login')));

const callToAction = computed(() => {
  const count = playlistItems.value.length;

  if (!count) {
    return t('Listen to stories');
  }

  return count === 1 ? t('Listen to 1 story') : t('Listen to {count} stories', { count });
});

const initializePlayer = async () => {
  try {
    if (!playlistItems.value.length) {
      destroy();
      return;
    }

    await create({ playlist: playlistItems.value }, {
      callToAction: callToAction.value,
      playerStyle: 'standard',
      showBottomWidget: false
    });
  } catch (err) {
    destroy();
  }
};

/* The player only exists for subscribers; everyone else gets the gate. */
watch(
  [() => canListenToAudio.value, () => playerContainer.value, () => playlistLoading.value, playlistItems],
  ([canListen, container, isPlaylistLoading]) => {
    if (!canListen || !container) {
      destroy();
      return;
    }

    hideLoginTooltip();

    if (isPlaylistLoading) {
      return;
    }

    initializePlayer();
  },
  { immediate: true, flush: 'post' }
);
</script>

<style scoped lang="scss">
.dn-audio-playlist {
  --dn-audio-playlist-bg: var(--color-brand-utility-50);
  --dn-audio-playlist-fg: var(--color-brand-utility-700);
  --dn-audio-playlist-subtle: var(--color-teaser-brand-subtle-fg);
  --dn-audio-playlist-player-bg: var(--color-brand-utility-0);
  --dn-audio-playlist-player-fg: var(--color-brand-utility-700);

  position: relative;
  display: grid;
  gap: 0.75rem;
  padding: 1rem 0.75rem;
  border-radius: 2px;
  background-color: var(--dn-audio-playlist-bg);
  color: var(--dn-audio-playlist-fg);

  details {
    display: contents;
  }
}

.dn-audio-playlist__kicker {
  align-items: center;
  color: var(--dn-audio-playlist-subtle);
  display: flex;
  font-family: var(--typography-utility-s-font-family);
  font-size: var(--typography-utility-s-font-size);
  line-height: 1;
  margin: 0 3rem 0 3px;
  text-transform: uppercase;

  &::before {
    block-size: 0.75rem;
    border-right: 1px solid var(--dn-audio-playlist-subtle);
    content: '';
    display: block;
    flex-shrink: 0;
    inline-size: 0;
    margin-right: 0.5rem;
  }

  /* Ear/audio glyph, inlined so the kicker needs no extra request. */
  &::after {
    mask-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAgNS45NTUyMUMwIDIuNjY2NjEgMi42ODY2NyAwIDYgMEM5LjMxMzMzIDAgMTIgMi42NjY2MSAxMiA1Ljk1NTIxVjExLjA0MTRIMTEuOTk3OVYxMS4zNjExQzExLjk5NzkgMTEuNzEyNSAxMS42Mzc5IDEyIDExLjE5OCAxMkg5LjE5ODA1QzguNzU4MDcgMTIgOC4zOTgwOCAxMS43MTI1IDguMzk4MDggMTEuMzYxMVY3Ljc1NzU1QzguMzk4MDggNy40MDYxNCA4Ljc1ODA3IDcuMTE4NjIgOS4xOTgwNSA3LjExODYySDExVjUuOTU1MjFDMTEgMy4yMjU5OCA4Ljc2ODE4IDAuOTk5OTk2IDYgMC45OTk5OTZDMy4yMzE4MiAwLjk5OTk5NiAwLjk5OTk1OCAzLjIyNTk4IDAuOTk5OTU4IDUuOTU1MjFWNy4xMTg2MkgyLjc5OTg4QzMuMjM5ODYgNy4xMTg2MiAzLjU5OTg1IDcuNDA2MTQgMy41OTk4NSA3Ljc1NzU1VjExLjM2MTFDMy41OTk4NSAxMS43MTI1IDMuMjM5ODYgMTIgMi43OTk4OCAxMkgwLjc5OTk2NkMwLjM1OTk4NSAxMiAwIDExLjcxMjUgMCAxMS4zNjExVjUuOTU1MjFaIiBmaWxsPSIjQTZCOEQ4Ii8+Cjwvc3ZnPgo=');
    mask-repeat: no-repeat;
    mask-size: 0.75rem;
    background-color: currentcolor;
    block-size: 0.75rem;
    content: '';
    display: block;
    flex-shrink: 0;
    inline-size: 0.75rem;
    margin-right: 0.5rem;
    order: -1;
  }
}

.dn-audio-playlist__title {
  color: var(--dn-audio-playlist-fg);
  font-family: var(--typography-editorial-subhead-min-font-family);
  font-size: var(--typography-editorial-subhead-min-font-size);
  font-weight: var(--typography-editorial-subhead-min-font-weight);
  line-height: var(--typography-editorial-subhead-min-line-height);
  letter-spacing: var(--typography-editorial-subhead-min-letter-spacing);
  margin: 0;
  text-wrap: pretty;
}

.dn-audio-playlist__player-wrap {
  position: relative;
  min-width: 0;
}

.dn-audio-playlist__player {
  min-width: 0;
  width: 100%;
  overflow: visible;

  :deep(.bwp) {
    width: 100% !important;
    max-width: none !important;
  }
}

.dn-audio-playlist__placeholder {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  background: var(--dn-audio-playlist-player-bg);
  color: var(--dn-audio-playlist-player-fg);
  box-sizing: border-box;
}

.dn-audio-playlist__placeholder-button {
  flex: 0 0 auto;
  inline-size: 40px;
  block-size: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: var(--dn-audio-playlist-player-fg);
  border-radius: 50%;
  appearance: none;
  overflow: hidden;
  cursor: pointer;
}

.dn-audio-playlist__placeholder-icon {
  inline-size: 40px;
  block-size: 40px;
  display: block;
  color: var(--dn-audio-playlist-player-bg);
}

.dn-audio-playlist__placeholder-title {
  margin: 0;
  font-family: var(--typography-utility-m-font-family, Inter, sans-serif);
  font-size: 14px;
  line-height: 1.2;
  color: var(--dn-audio-playlist-player-fg);
}

.dn-audio-playlist__disclaimer-button {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 50;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  list-style: none;

  &:hover {
    opacity: 0.8;
  }
}

.dn-audio-playlist__disclaimer-icon {
  block-size: 1.7rem;
  inline-size: 1.7rem;
}

.dn-audio-playlist__disclaimer-icon-stroke {
  stroke: var(--dn-audio-playlist-fg);
}

.dn-audio-playlist__disclaimer-icon-fill {
  fill: var(--dn-audio-playlist-fg);
}

.dn-audio-playlist__disclaimer-text {
  margin: 0.25rem 0.125rem 0 0;
  color: var(--dn-audio-playlist-fg);
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1.33;
}

.dn-audio-playlist__disclaimer-link {
  color: var(--dn-audio-playlist-fg);
  font-size: inherit;
  text-decoration: underline;

  &:hover {
    color: var(--dn-audio-playlist-subtle);
  }
}

.dn-audio-playlist__login-tooltip {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  pointer-events: none;
}

:deep(.dn-audio-playlist__login-tooltip .label-container) {
  width: min(320px, calc(100vw - 24px));
  height: 3rem;
  pointer-events: none !important;
}

:deep(.dn-audio-playlist__login-tooltip .tooltip) {
  background: var(--dn-audio-playlist-player-bg);
  border-radius: 14px;
  width: min(320px, calc(100vw - 24px));
  max-width: min(320px, calc(100vw - 24px));
  padding: 0;
  box-sizing: border-box;
  pointer-events: auto;
}

:deep(.dn-audio-playlist__login-tooltip .arrow::before) {
  background: var(--dn-audio-playlist-player-bg);
}

:deep(.dn-audio-playlist__login-tooltip-card) {
  position: relative;
  padding: 16px;
  color: var(--dn-audio-playlist-player-fg);
  font-family: var(--typography-editorial-subhead-min-font-family);
}

:deep(.dn-audio-playlist__login-tooltip-close) {
  position: absolute;
  top: 8px;
  right: 8px;
  border: 0;
  background: transparent;
  color: var(--dn-audio-playlist-player-fg);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
}

:deep(.dn-audio-playlist__login-tooltip-title) {
  margin: 0 28px 8px 0;
  font-size: var(--typography-editorial-subhead-min-font-size);
  line-height: var(--typography-editorial-subhead-min-line-height);
  letter-spacing: var(--typography-editorial-subhead-min-letter-spacing);
  font-weight: var(--typography-editorial-subhead-min-font-weight, 700);
  font-family: var(--typography-editorial-subhead-min-font-family);
}

:deep(.dn-audio-playlist__login-tooltip-text) {
  margin: 0;
  font-size: 14px;
  line-height: 1.35;
  font-family: inherit;
}

:deep(.dn-audio-playlist__login-tooltip-cta) {
  display: block !important;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 6px;
  text-align: center;
  text-decoration: none;
  background: var(--dn-audio-playlist-player-fg);
  color: var(--dn-audio-playlist-player-bg);
  border: 1px solid var(--color-button-primary-default-border, transparent);
  font-size: var(--typography-utility-button-font-size, 16px);
  line-height: var(--typography-utility-button-line-height, 1.2);
  font-family: inherit;
  font-weight: var(--typography-utility-button-font-weight, 600);

  &:hover {
    opacity: 0.85;
  }

  &:active {
    opacity: 0.7;
  }
}

@media only screen and (width >= 420px) {
  .dn-audio-playlist {
    padding: 1rem;
  }

  .dn-audio-playlist__kicker {
    font-family: var(--typography-utility-m-font-family);
    font-size: var(--typography-utility-m-font-size);
    letter-spacing: 0.02em;
  }

  .dn-audio-playlist__title {
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
}

@media only screen and (width <= 767px) {
  :deep(.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp .secondary-buttons) {
    display: flex !important;
  }
}

@media only screen and (width <= 430px) {
  :deep(.dn-audio-playlist__login-tooltip .label-container) {
    width: 100%;
  }

  :deep(.dn-audio-playlist__login-tooltip .tooltip) {
    width: 100% !important;
    max-width: 100% !important;
  }
}

.theme-dark .dn-audio-playlist {
  --dn-audio-playlist-bg: var(--color-brand-utility-700);
  --dn-audio-playlist-fg: var(--color-brand-utility-0);
  --dn-audio-playlist-player-bg: var(--color-brand-utility-50);
  --dn-audio-playlist-player-fg: var(--color-brand-utility-700);
}
</style>
